import { sharedDocumentFieldsSchema } from "./document";
import { themeType } from "./global";
import { z } from "zod";

export const creditNoteStatusEnum = z.enum([
  "draft",
  "not-sent",
  "sent",
  "overdue",
  "partially-paid",
  "paid",
  "cancelled",
]);
export type CreditNoteStatusType = z.infer<typeof creditNoteStatusEnum>;

export const baseCreditNoteSchema = sharedDocumentFieldsSchema.merge(themeType).extend({
  creditNoteNumber: z.string().min(1, { message: "Credit note number is required" }),
  status: creditNoteStatusEnum.default("draft").optional(),
  dueDate: z.date().nullable(),
  invoiceIds: z.array(z.string()).optional().nullable(),
  projectId: z.string().optional().nullable(),
});

export const createCreditNoteSchema = baseCreditNoteSchema;

export const editCreditNoteSchema = baseCreditNoteSchema.extend({
  id: z.string(),
});

export const getCreditNoteSchema = z.object({
  id: z.string(),
});

export const EditCreditNotePageSchema = z.object({
  id: z.string(),
});

export const updateCreditNoteStatusSchema = z.object({
  status: creditNoteStatusEnum,
});
export const getCreditNotesParamsSchema = z.object({
  // Entity filters
  invoiceId: z.string().optional(),
  relationId: z.string().optional(),
  projectId: z.string().optional(),
  // Text search filters
  creditNoteNumber: z.string().optional(),
  client: z.string().optional(),
  // Status filter
  status: z
    .union([creditNoteStatusEnum, z.array(creditNoteStatusEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  // Date range filters
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  dueDateFrom: z.coerce.date().optional(),
  dueDateTo: z.coerce.date().optional(),
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
  paymentDateFrom: z.coerce.date().optional(),
  paymentDateTo: z.coerce.date().optional(),
});

export type ZodCreateCreditNoteSchema = z.infer<typeof createCreditNoteSchema>;
export type ZodEditCreditNoteSchema = z.infer<typeof editCreditNoteSchema>;
export type ZodGetCreditNotesParams = z.infer<typeof getCreditNotesParamsSchema>;

export const createCreditNoteSchemaDefaultValues: ZodCreateCreditNoteSchema = {
  invoiceIds: [],
  status: "draft",

  organizationName: "",
  organizationLogo: "",
  organizationAddressLine1: "",
  organizationAddressLine2: "",
  organizationCity: "",
  organizationState: "",
  organizationZipcode: "",
  organizationCountry: "",

  relationId: "",
  relationName: "",
  relationAddressLine1: "",
  relationAddressLine2: "",
  relationCity: "",
  relationState: "",
  relationZipcode: "",
  relationCountry: "",

  model: "default",
  language: "en",
  primaryColor: "#635CFF",
  textBeforeItems: "",
  textAfterItems: "",
  footer: "",

  currency: "USD",
  exchangeRates: null,
  creditNoteNumber: "",
  date: new Date(), // now
  dueDate: null,
  showItemsWithTax: false,
  total: 0,
  subtotal: 0,

  items: [],
  taxes: [],
};

export const creditNoteSchema = baseCreditNoteSchema.extend({
  id: z.string(),
  paidAt: z.date().nullable(),
  paymentDate: z.date().nullable(),
  sentDate: z.date().optional().nullable(),
  openDate: z.date().optional().nullable(),
  cancelDate: z.date().optional().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CreditNoteSchema = z.infer<typeof creditNoteSchema>;
